
import { Box, Button, Chip, Typography } from "@mui/material";
import type { ColumnDef } from "@tanstack/react-table";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "../../../../../redux/types";
import { startSortBrandLines } from "@/redux/actions/lines";
import ReactTable from "../../../../shared/components/table/ReactTable";
import CustomSnackBar from "@/contexts/shared/components/CustomSnackBar";
import { LineActionsMenu } from "./LineActionsMenu";


interface Line {
  id: string;
  name: string;
  status: number;
}


const LinesTable: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["global"]);
  const { brandSelected } = useSelector((state: IRootState) => state.brand);
  const [lines, setLines] = useState<Line[]>(brandSelected?.lines || []);
  const [newLinesOrder, setNewLinesOrder] = useState<{ id: number; order: number }[]>([]);
  const [isOrderModified, setIsOrderModified] = useState(false);
  const [messages, setMessages] = useState<string[]>([]);
  const [errors, setErrors] = useState<boolean[]>([]);

  useEffect(() => {
    if (brandSelected?.lines) {
      const sorted = [...brandSelected.lines].sort((a, b) => a.order - b.order);
      setLines(sorted);
    }
  }, [brandSelected]);

  const handleDragChange = (draggedIndex: number, targetIndex: number) => {
    const updated = [...lines];
    const [draggedRow] = updated.splice(draggedIndex, 1);
    updated.splice(targetIndex, 0, draggedRow);
    setLines(updated);

    const orderedLines = updated.map((line, index) => ({
      id: Number(line.id),
      order: index + 1,
    }));
    setNewLinesOrder(orderedLines);
    setIsOrderModified(true);
  };

  const columns: ColumnDef<Line>[] = [
    {
      accessorKey: "name",
      header: t("name"),
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: "status",
      header: t("status"),
      cell: (info) => (
        <Chip
          size="medium"
          color={info.getValue() === 1 ? "primary" : "secondary"}
          label={info.getValue() === 1 ? t("active") : t("inactive")}
        />
      ),
    },
{
  id: "actions",
  header: "",
  cell: ({ row }) => {
    const line = row.original;
    return (
      <LineActionsMenu
        line={line}
        onDelete={(id) =>
          setLines((prev) => prev.filter((l) => l.id !== id))
        }
        onToggleStatus={(id) =>
          setLines((prev) =>
            prev.map((l) =>
              l.id === id ? { ...l, status: l.status === 1 ? 0 : 1 } : l
            )
          )
        }
      />
    );
  },
}
  ];

  const handleSaveOrder = () => {
    setIsOrderModified(false);
    dispatch(startSortBrandLines({ sortedBrandLines: newLinesOrder }));
    setMessages([t("order-success")]);
    setErrors([false]);
  };

  return (
    <Box mt={2} mb={2}>
      <Typography variant="h5" gutterBottom>
        {t("lines")}
      </Typography>
      <ReactTable
        columns={columns}
        data={lines}
        rowId="id"
        draggableRow
        enableSearch
        onDraggableRowChange={handleDragChange}
        getDataOrdered={(ordered) => console.log("Ordered data:", ordered)}
      />
      {isOrderModified && (
        <Button variant="contained" onClick={handleSaveOrder}>
          {t("save-order")}
        </Button>
      )}

      <CustomSnackBar errors={errors} msgs={messages} />
    </Box>
  );
};

export default LinesTable;
